import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useStreaks } from "@/hooks/useStreaks";
import { useWallet } from "@/hooks/useWallet";
import { Flame, BookOpen, Target, Coins } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import type { AnyRow } from "@/lib/rowTypes";

interface Stats {
  lessonsCompleted: number;
  avgScore: number | null;
}

export default function ProgressStatsRow() {
  const { user } = useAuth();
  const { streaks } = useStreaks();
  const { balance } = useWallet();
  const [stats, setStats] = useState<Stats | null>(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const [{ count: lessons }, { data: attempts }] = await Promise.all([
          supabase
            .from("user_progress")
            .select("lesson_id", { count: "exact", head: true })
            .eq("user_id", user.id)
            .eq("completed", true),
          supabase
            .from("quiz_attempts")
            .select("score_percent")
            .eq("user_id", user.id)
            .order("created_at", { ascending: false })
            .limit(20),
        ]);

        const scores = (attempts || [])
          .map((a: AnyRow) => Number(a.score_percent))
          .filter((n) => Number.isFinite(n));
        const avg =
          scores.length > 0
            ? Math.round(scores.reduce((s, v) => s + v, 0) / scores.length)
            : null;

        if (!cancelled) setStats({ lessonsCompleted: lessons ?? 0, avgScore: avg });
      } catch (err) {
        console.error("ProgressStatsRow error:", err);
        if (!cancelled) setStats({ lessonsCompleted: 0, avgScore: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (!stats) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-20 rounded-2xl" />
        ))}
      </div>
    );
  }

  const tiles = [
    {
      label: "Day Streak",
      value: `${streaks.login_streak}`,
      sub: `Longest ${streaks.longest_login_streak}`,
      icon: Flame,
      tint: "from-orange-400/30 to-pink-400/10 text-orange-300",
    },
    {
      label: "Lessons Done",
      value: `${stats.lessonsCompleted}`,
      sub: "Total completed",
      icon: BookOpen,
      tint: "from-primary/30 to-primary/0 text-primary",
    },
    {
      label: "Avg Quiz",
      value: stats.avgScore !== null ? `${stats.avgScore}%` : "—",
      sub: stats.avgScore !== null ? "Last 20" : "Take a quiz",
      icon: Target,
      tint: "from-accent/30 to-accent/0 text-accent",
    },
    {
      label: "Balance",
      value: `$${Number(balance ?? 0).toLocaleString()}`,
      sub: "Sim wallet",
      icon: Coins,
      tint: "from-secondary/30 to-secondary/0 text-secondary",
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {tiles.map((t) => {
        const Icon = t.icon;
        return (
          <div
            key={t.label}
            className="relative overflow-hidden rounded-2xl border border-border/60 bg-card/80 backdrop-blur-md p-4 hover:border-primary/50 transition-colors"
          >
            <div className={`absolute -top-6 -right-6 w-20 h-20 rounded-full bg-gradient-to-br ${t.tint.split(" ").slice(0, 2).join(" ")} blur-2xl opacity-60`} />
            <div className="relative flex items-center gap-2 mb-2">
              <Icon className={`w-3.5 h-3.5 ${t.tint.split(" ").slice(-1)[0]}`} />
              <span className="text-[10px] font-heading font-semibold uppercase tracking-wider text-foreground/80">
                {t.label}
              </span>
            </div>
            <p className="relative font-heading font-extrabold text-2xl text-foreground leading-none">
              {t.value}
            </p>
            <p className="relative text-[10px] text-foreground/65 mt-1 font-medium">{t.sub}</p>
          </div>
        );
      })}
    </div>
  );
}